import clsx from 'clsx'
import {useFormik} from 'formik'
import {FC, useCallback, useEffect, useMemo, useState} from 'react'
import {resetPasswordToken, validateUserToken} from '../core/_request'
import {ResetPasswordRequestType} from '../core/_model'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {InputGroup} from 'react-bootstrap'
import {useAuth} from '../../auth'
import {GoogleReCaptcha, GoogleReCaptchaProvider} from 'react-google-recaptcha-v3'
import {intervalRefreshRecaptcha} from '../../../../_metronic/helpers'
import LoadingButtonWrapper from '../../../components/LoadingButtonWrapper'
import {useAppIntl} from '../../../i18n'
import {createForgotPasswordResetSchema} from '../../auth/authSchemas'

const initial: ResetPasswordRequestType = {
  verifyPassword: '',
  newPassword: '',
  token: '',
}

const isUseCaptcha = process.env.REACT_APP_USE_CAPTCHA === 'true'
const captchaKey = process.env.REACT_APP_CAPTCHA_TOKEN ?? ''

const ForgotPasswordReset: FC = () => {
  const {formatMessage} = useAppIntl()
  const validationSchema = useMemo(
    () => createForgotPasswordResetSchema(formatMessage),
    [formatMessage]
  )
  const [loading, setLoading] = useState<boolean>(false)
  const [visible1, setVisible1] = useState<boolean>(false)
  const [visible2, setVisible2] = useState<boolean>(false)
  const [isError, setError] = useState<boolean>(false)
  const [isErrorForm, setErrorForm] = useState<boolean>(false)
  const [isSuccess, setSuccess] = useState<boolean>(false)
  const [isInit, setIsInit] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string>('')
  const [refreshCaptcha, setRefreshCaptcha] = useState<boolean>(false)

  const onVerify = useCallback((token: string) => {
    setCaptchaToken(token)
  }, [])

  useEffect(() => {
    const intervalId = setInterval(() => {
      setRefreshCaptcha((r) => !r)
    }, intervalRefreshRecaptcha)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  const {search} = useLocation()
  const url = useMemo(() => new URLSearchParams(search), [search])
  const navigate = useNavigate()

  const {logout} = useAuth()

  const formik = useFormik({
    initialValues: initial,
    validationSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      try {
        setLoading(true)
        setError(false)
        setStatus(undefined)

        const token = url.get('t') ?? ''

        let request: ResetPasswordRequestType = {
          ...values,
          token: token,
          captchaToken: '',
        }

        if (isUseCaptcha) {
          request = {
            ...values,
            token: token,
            captchaToken: captchaToken,
          }
        }

        const response = await resetPasswordToken(request)

        if (response.code === '1003') {
          setStatus(response.message)
          setErrorForm(true)
        } else {
          setSuccess(true)
          setStatus(formatMessage({id: 'auth.forgot.reset.successRedirect'}))
          setErrorForm(false)

          setTimeout(() => {
            navigate('/auth')
          }, 3000)
        }
      } catch (error: any) {
        setStatus(error.message)
        setErrorForm(true)
        setRefreshCaptcha(!refreshCaptcha)
      } finally {
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  useEffect(() => {
    logout()
    setLoading(true)
    const token = url.get('t')

    if (!token) {
      formik.setStatus(formatMessage({id: 'auth.forgot.reset.invalidToken'}))
      setError(true)
      setIsInit(true)
      setLoading(false)
      return
    }

    validateUserToken(token ?? '')
      .then((response) => {
        if (response.code === '1003') {
          setError(true)
          formik.setStatus(response.message)
        }
      })
      .catch((err: any) => {
        formik.setStatus(err.message)
        setError(true)
      })
      .finally(() => {
        setIsInit(true)
        setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url])

  return isError ? (
    <>
      <div className='alert alert-danger mb-10'>
        <div className='alert-text font-weight-bold'>{formik.status}</div>
      </div>
      <Link to='/auth'>{formatMessage({id: 'auth.forgot.reset.backToLogin'})}</Link>
    </>
  ) : isSuccess && !isErrorForm && formik.status ? (
    <div className='mb-lg-15 alert alert-success'>
      <div className='alert-text font-weight-bold'>{formik.status}</div>
    </div>
  ) : isInit ? (
    <>
      <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
        <div className='text-center mb-8'>
          <h1 className='text-dark fw-bolder mb-3'>
            {formatMessage({id: 'auth.forgot.reset.title'})}
          </h1>
          <div className='text-gray-500 fw-semibold fs-6'>
            {formatMessage({id: 'auth.forgot.reset.subtitle'})}
          </div>
        </div>

        {formik.status && isErrorForm && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        {formik.status && !isErrorForm && (
          <div className='mb-lg-15 alert alert-success'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        <div className='fv-row mb-4'>
          <label className='form-label fw-bolder text-dark fs-6'>
            {formatMessage({id: 'auth.forgot.reset.newPassword'})}
          </label>
          <div className='position-relative'>
            <InputGroup>
              <input
                placeholder={formatMessage({id: 'auth.forgot.reset.newPassword.placeholder'})}
                {...formik.getFieldProps('newPassword')}
                type={visible1 ? 'text' : 'password'}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.newPassword && formik.errors.newPassword,
                  },
                  {
                    'is-valid': formik.touched.newPassword && !formik.errors.newPassword,
                  }
                )}
                tabIndex={1}
                autoComplete='off'
              />

              <InputGroup.Text role='button' onClick={() => setVisible1((e) => !e)}>
                <i className={!visible1 ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
              </InputGroup.Text>
            </InputGroup>

            {formik.touched.newPassword && formik.errors.newPassword && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.newPassword}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='fv-row mb-3'>
          <label className='form-label fw-bolder text-dark fs-6'>
            {formatMessage({id: 'auth.forgot.reset.confirmPassword'})}
          </label>
          <div className='position-relative'>
            <InputGroup>
              <input
                placeholder={formatMessage({
                  id: 'auth.forgot.reset.confirmPassword.placeholder',
                })}
                {...formik.getFieldProps('verifyPassword')}
                type={visible2 ? 'text' : 'password'}
                className={clsx(
                  'form-control bg-transparent',
                  {
                    'is-invalid': formik.touched.verifyPassword && formik.errors.verifyPassword,
                  },
                  {
                    'is-valid': formik.touched.verifyPassword && !formik.errors.verifyPassword,
                  }
                )}
                tabIndex={2}
                autoComplete='off'
              />

              <InputGroup.Text role='button' onClick={() => setVisible2((e) => !e)}>
                <i className={!visible2 ? 'fas fa-eye-slash' : 'fas fa-eye'}></i>
              </InputGroup.Text>
            </InputGroup>

            {formik.touched.verifyPassword && formik.errors.verifyPassword && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.verifyPassword}</span>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-3'>
          <div />
          <Link to='/auth' className='link-primary'>
            {formatMessage({id: 'auth.forgot.request.loginInstead'})}
          </Link>
        </div>

        <div className='d-grid mb-3'>
          <LoadingButtonWrapper isLoading={loading}>
            <button
              type='submit'
              className='btn btn-danger'
              disabled={formik.isSubmitting || !formik.isValid}
            >
              <span className='indicator-label'>{formatMessage({id: 'common.action.submit'})}</span>
            </button>
          </LoadingButtonWrapper>
        </div>
      </form>
      {isUseCaptcha && (
        <GoogleReCaptchaProvider reCaptchaKey={captchaKey}>
          <GoogleReCaptcha
            onVerify={onVerify}
            refreshReCaptcha={refreshCaptcha}
            action='reset_password'
          />
        </GoogleReCaptchaProvider>
      )}
    </>
  ) : (
    <></>
  )
}

export default ForgotPasswordReset
